import React, { useEffect, useState } from 'react';
import axios from 'axios';

/* istanbul ignore next -- REACT_APP_BACKEND_URL is inlined at build time */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const emptySearchState = () => ({
  query: '',
  orientation: '',
  source: 'unsplash',
  page: 1,
  results: null,
  relatedStack: [],
});

const SOURCE_OPTIONS = [
  { value: 'unsplash', label: 'Unsplash (default)' },
  { value: 'pixabay', label: 'Pixabay' },
  { value: 'pexels', label: 'Pexels' },
];

const SEARCH_THEMES = [
  { label: 'Birds', query: 'birds wildlife', orientation: '' },
  { label: 'Animals', query: 'wild animals', orientation: '' },
  { label: 'Landscape', query: 'landscape scenery', orientation: 'landscape' },
  { label: 'Urban', query: 'urban cityscape street', orientation: '' },
  { label: 'Still life', query: 'still life objects', orientation: '' },
  { label: 'Portraits', query: 'portrait face', orientation: 'portrait' },
  { label: 'Flowers', query: 'flowers botanical', orientation: '' },
  { label: 'Ocean', query: 'ocean seascape waves', orientation: 'landscape' },
];

function IconButton({ title, onClick, disabled, children, className = '' }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center p-2 border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

function ExternalLinkIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function SimilarIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function PaletteIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export { emptySearchState };

export default function ReferenceSearchPage({
  searchState,
  onSearchStateChange,
  onReferenceAnalyzed,
  showToast,
  userToken,
}) {
  const { query, orientation, source, page, results, relatedStack } = searchState;
  const [loading, setLoading] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [analyzingPhotoOfDay, setAnalyzingPhotoOfDay] = useState(false);
  const [availableProviders, setAvailableProviders] = useState(null);
  const [photoOfDay, setPhotoOfDay] = useState(null);
  const [photoOfDayLoading, setPhotoOfDayLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/reference-providers`)
      .then((response) => {
        if (!cancelled) {
          setAvailableProviders(response.data.providers || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableProviders([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPhotoOfDayLoading(true);
    axios
      .get(`${API_URL}/api/reference-photo-of-the-day`)
      .then((response) => {
        if (!cancelled) {
          setPhotoOfDay(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPhotoOfDay(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPhotoOfDayLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isProviderAvailable = (provider) => {
    if (!availableProviders) return true;
    if (provider === 'unsplash') {
      return availableProviders.length > 0;
    }
    return availableProviders.includes(provider);
  };

  const patchSearchState = (partial) => {
    onSearchStateChange({ ...searchState, ...partial });
  };

  const runSearch = async (searchQuery, searchPage = 1, options = {}) => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      showToast('Enter a search term.', 'error');
      return;
    }
    const searchOrientation =
      options.orientation !== undefined ? options.orientation : orientation;
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/search-references`, {
        query: trimmed,
        page: searchPage,
        per_page: 20,
        orientation: searchOrientation || null,
        source: source === 'unsplash' ? null : source,
      });
      onSearchStateChange({
        query: trimmed,
        orientation: searchOrientation,
        source,
        page: searchPage,
        results: response.data,
        relatedStack: [],
      });
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      showToast(detail || 'Search failed.', 'error');
      patchSearchState({ results: null });
    } finally {
      setLoading(false);
    }
  };

  const searchTheme = (theme) => {
    runSearch(theme.query, 1, { orientation: theme.orientation || '' });
  };

  const runRelated = async (image) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/search-references/related`,
        {
          provider: image.provider,
          id: image.id,
          tags: image.tags || [],
          query: results?.query || query,
          page: 1,
          per_page: 20,
        }
      );
      onSearchStateChange({
        ...searchState,
        relatedStack: [...relatedStack, results],
        results: response.data,
        page: 1,
      });
    } catch (error) {
      showToast(
        error.response?.data?.detail || 'Could not load related photos.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    const prev = relatedStack[relatedStack.length - 1];
    if (!prev) return;
    onSearchStateChange({
      ...searchState,
      results: prev,
      relatedStack: relatedStack.slice(0, -1),
      page: prev.page || 1,
    });
  };

  const analyzeImage = async (image, { trackPhotoOfDay = false } = {}) => {
    if (trackPhotoOfDay) {
      setAnalyzingPhotoOfDay(true);
    } else {
      setAnalyzingId(image.id);
    }
    try {
      const headers = {};
      if (userToken) {
        headers.Authorization = `Bearer ${userToken}`;
      }
      const response = await axios.post(
        `${API_URL}/api/analyze-reference`,
        {
          provider: image.provider,
          id: image.id,
          image_url: image.image_url,
        },
        { headers }
      );
      onReferenceAnalyzed(response.data, searchState);
    } catch (error) {
      showToast(
        error.response?.data?.detail || 'Could not analyze this photo.',
        'error'
      );
    } finally {
      if (trackPhotoOfDay) {
        setAnalyzingPhotoOfDay(false);
      } else {
        setAnalyzingId(null);
      }
    }
  };

  const photoOfDayImage = photoOfDay?.image;

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <section className="reference-photo-of-day mb-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Reference photo of the day</h2>
            <p className="text-gray-600 mt-1">
              A new inspiration each day, shared for every visitor.
            </p>
          </div>
          {photoOfDay?.source && (
            <span className="text-sm text-gray-500" title="Photo provider">
              via {photoOfDay.source}
            </span>
          )}
        </div>

        {photoOfDayLoading && (
          <div className="reference-photo-of-day-frame reference-photo-of-day-loading">
            <SpinnerIcon />
          </div>
        )}

        {!photoOfDayLoading && photoOfDayImage && (
          <div className="reference-photo-of-day-frame">
            <a
              href={photoOfDayImage.page_url}
              target="_blank"
              rel="noopener noreferrer"
              title={`View on ${photoOfDayImage.provider}: ${photoOfDayImage.attribution}`}
            >
              <img
                src={photoOfDayImage.image_url}
                alt={photoOfDayImage.attribution}
                title={photoOfDayImage.attribution}
                className="reference-photo-of-day-image"
              />
            </a>
            <div className="reference-photo-of-day-meta">
              <p className="text-sm text-gray-700">
                <a
                  href={photoOfDayImage.page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open photographer page on ${photoOfDayImage.provider}`}
                  className="underline"
                >
                  {photoOfDayImage.attribution}
                </a>
                {photoOfDay?.query ? (
                  <span className="text-gray-500"> · inspired by “{photoOfDay.query}”</span>
                ) : null}
              </p>
              <div className="flex flex-wrap gap-2">
                <IconButton
                  title="View on site"
                  onClick={() => window.open(photoOfDayImage.page_url, '_blank', 'noopener')}
                  className="hover:bg-gray-50 text-gray-700"
                >
                  <ExternalLinkIcon />
                </IconButton>
                <IconButton
                  title={
                    analyzingPhotoOfDay ? 'Analyzing colors…' : 'Analyze colors'
                  }
                  onClick={() => analyzeImage(photoOfDayImage, { trackPhotoOfDay: true })}
                  disabled={analyzingPhotoOfDay}
                  className="bg-purple-600 text-white border-purple-600 hover:bg-purple-700"
                >
                  {analyzingPhotoOfDay ? <SpinnerIcon /> : <PaletteIcon />}
                </IconButton>
              </div>
            </div>
          </div>
        )}
      </section>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Find a reference photo
      </h2>
      <p className="text-gray-600 mb-6">
        Search stock photos, then analyze colors to find matching art supplies.
      </p>

      <form
        className="flex flex-wrap gap-3 mb-6"
        onSubmit={(e) => {
          e.preventDefault();
          runSearch(query, 1);
        }}
      >
        <input
          type="search"
          value={query}
          onChange={(e) => patchSearchState({ query: e.target.value })}
          placeholder="e.g. butterfly, ocean, portrait"
          title="Search stock photos by keyword"
          className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg"
        />
        <select
          value={source}
          onChange={(e) => patchSearchState({ source: e.target.value })}
          title="Choose which photo library to search"
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          {SOURCE_OPTIONS.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={!isProviderAvailable(option.value)}
            >
              {option.label}
              {!isProviderAvailable(option.value) ? ' (unavailable)' : ''}
            </option>
          ))}
        </select>
        <select
          value={orientation}
          onChange={(e) => patchSearchState({ orientation: e.target.value })}
          title="Filter by photo orientation"
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">Any orientation</option>
          <option value="landscape">Landscape</option>
          <option value="portrait">Portrait</option>
          <option value="squarish">Squarish</option>
        </select>
        <button
          type="submit"
          disabled={loading}
          title="Search"
          aria-label="Search"
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {loading ? <SpinnerIcon /> : <SearchIcon />}
          <span className="sr-only">Search</span>
        </button>
      </form>

      <div className="mb-6">
        <p className="text-sm font-medium text-gray-700 mb-2">Popular subjects</p>
        <div className="reference-theme-list">
          {SEARCH_THEMES.map((theme) => (
            <button
              key={theme.label}
              type="button"
              disabled={loading}
              onClick={() => searchTheme(theme)}
              title={`Search for ${theme.label.toLowerCase()}`}
              className="reference-theme-chip"
            >
              {theme.label}
            </button>
          ))}
        </div>
      </div>

      {relatedStack.length > 0 && (
        <button
          type="button"
          onClick={goBack}
          title="Back to previous search results"
          className="inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 mb-4"
        >
          <ChevronLeftIcon />
          Back to search results
        </button>
      )}

      {results?.images?.length > 0 && (
        <>
          <p className="text-sm text-gray-500 mb-3">
            Showing results from {results.source}
            {results.query ? ` for “${results.query}”` : ''}
          </p>
          <div className="reference-gallery">
            {results.images.map((image) => (
              <div key={`${image.provider}-${image.id}`} className="space-y-2">
                <a
                  href={image.page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`View on ${image.provider}: ${image.attribution}`}
                >
                  <img
                    src={image.thumbnail_url}
                    alt={image.attribution}
                    title={image.attribution}
                    className="reference-image w-full"
                  />
                </a>
                <p className="text-xs text-gray-600">
                  <a
                    href={image.page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open photographer page on ${image.provider}`}
                    className="underline"
                  >
                    {image.attribution}
                  </a>
                </p>
                <div className="flex flex-wrap gap-2">
                  <IconButton
                    title="View on site"
                    onClick={() => window.open(image.page_url, '_blank', 'noopener')}
                    className="hover:bg-gray-50 text-gray-700"
                  >
                    <ExternalLinkIcon />
                  </IconButton>
                  <IconButton
                    title="More like this"
                    onClick={() => runRelated(image)}
                    disabled={loading}
                    className="hover:bg-gray-50 text-gray-700"
                  >
                    <SimilarIcon />
                  </IconButton>
                  <IconButton
                    title={analyzingId === image.id ? 'Analyzing colors…' : 'Analyze colors'}
                    onClick={() => analyzeImage(image)}
                    disabled={analyzingId === image.id}
                    className="bg-purple-600 text-white border-purple-600 hover:bg-purple-700"
                  >
                    {analyzingId === image.id ? <SpinnerIcon /> : <PaletteIcon />}
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <IconButton
              title="Previous page"
              disabled={page <= 1 || loading}
              onClick={() => runSearch(results.query || query, page - 1)}
              className="hover:bg-gray-50 text-gray-700 px-3"
            >
              <ChevronLeftIcon />
            </IconButton>
            <span className="self-center text-sm text-gray-500" title={`Page ${page}`}>
              Page {page}
            </span>
            <IconButton
              title="Next page"
              disabled={loading || results.images.length < 20}
              onClick={() => runSearch(results.query || query, page + 1)}
              className="hover:bg-gray-50 text-gray-700 px-3"
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
        </>
      )}

      {results && results.images?.length === 0 && (
        <p className="text-gray-600">
          No photos found for “{results.query}”. Try a different search term.
        </p>
      )}

      <p className="text-xs text-gray-400 mt-8">
        Photos provided by{' '}
        <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" title="Unsplash">
          Unsplash
        </a>
        ,{' '}
        <a href="https://pixabay.com" target="_blank" rel="noopener noreferrer" title="Pixabay">
          Pixabay
        </a>
        , and{' '}
        <a href="https://pexels.com" target="_blank" rel="noopener noreferrer" title="Pexels">
          Pexels
        </a>
        .
      </p>
    </div>
  );
}
